import { EventBus } from 'components/eventBus';
import { TLogLevel } from 'components/logger/ILogger';
import { Logger, TLogger } from 'components/logger/Logger';
import { TLoggerEvents } from 'components/logger/Logger.events';
import { LoggerConsoleTransport } from 'components/logger/LoggerConsoleTransport';
import { defaultVideoElementControllerConfig } from 'components/videoElementController/VideoElementControllerConfig';
import { TMediaStream, IPlayerState } from 'types/common';
import { bind } from 'utils/bind';

import { IVideoElementControllerProps } from './IVideoElementController';
import {
  EVideoElementControllerBusEvent,
  IVideoElementControllerBusEventsMap,
  TVideoControllerEventBus,
} from './VideoElementController.events';
import { AutoplayController } from './components/autoplayController/AutoplayController';
import { CustomEventsEmitter } from './components/customEventsEmitter/CustomEventsEmitter';
import { PlaybackController } from './components/playbackController/PlaybackController';
import { RenderController } from './components/renderController/RenderController';
import { StateController } from './components/stateController/StateController';
import { StreamController } from './components/streamController/StreamController';
import { TimeUpdateController } from './components/timeUpdateController/TimeUpdateController';
import { VideoEventsHandler } from './components/videoEventsHandler/VideoEventsHandler';
import { INITIAL_STATE } from './constants/state';
import { StateManager } from '../stateManager/StateManager';

export class VideoElementController {
  private _playbackController: PlaybackController;
  private _streamController: StreamController;
  private _stateController: StateController;
  private _customEventsEmitter: CustomEventsEmitter;
  private _autoplayController: AutoplayController;
  private _timeUpdateController: TimeUpdateController;
  private _renderController: RenderController;
  private _updateVideoDurationId?: ReturnType<typeof setTimeout>;
  private _streamResetDelayId?: ReturnType<typeof setTimeout>;
  private _eventsHandler: VideoEventsHandler;
  private _state: StateManager<IPlayerState>;
  private _config: IVideoElementControllerProps = {
    ...defaultVideoElementControllerConfig.videoElementControllerConfig,
  };
  private _loggerTransport: LoggerConsoleTransport | null;

  private readonly _eventBus: TVideoControllerEventBus;
  private readonly _logger: TLogger;

  constructor() {
    this._eventBus = new EventBus<IVideoElementControllerBusEventsMap & TLoggerEvents>();
    this._loggerTransport = new LoggerConsoleTransport(this._eventBus);
    this._logger = Logger(this._eventBus, 'VideoController');

    this._logger.info('Created');
    this._logger.debug('Initial config', this._config);
    this._createControllers();
  }

  @bind
  public addEventListener(
    event: Parameters<CustomEventsEmitter['addEventListener']>[0],
    cb: Parameters<CustomEventsEmitter['addEventListener']>[1],
  ) {
    this._logger.verbose(`Attaching ${event} event listener`);
    this._customEventsEmitter.addEventListener(event, cb);
  }

  @bind
  public removeEventListener(
    event: Parameters<CustomEventsEmitter['removeEventListener']>[0],
    cb: Parameters<CustomEventsEmitter['removeEventListener']>[1],
  ) {
    this._logger.verbose(`Detaching ${event} event listener`);
    this._customEventsEmitter.removeEventListener(event, cb);
  }

  @bind
  public getIsPlaying() {
    return this._state.getState().isPlaying;
  }

  @bind
  public getVideoElement() {
    return this._state.getState().videoElement;
  }

  @bind
  public getCanvasElement() {
    return this._state.getState().canvasElement;
  }

  public updateConfig(config: Partial<IVideoElementControllerProps>) {
    this._config = { ...this._config, ...config };

    this._logger.debug('Config update received, the new one: ', this._config);

    if (config.isRemote) {
      this.attachVideoElementEvents();
    }

    this._syncConfig();
  }

  public setVideoElement(videoElement: HTMLVideoElement | null) {
    const currentVideoElement = this._state.getState().videoElement;

    if (videoElement === currentVideoElement) {
      return;
    }

    if (currentVideoElement) {
      this.detachVideoElementEvents(currentVideoElement);
    }

    this._logger.info('Video element set', { videoElement });
    this._renderController.setVideoElement(videoElement);
    this.attachVideoElementEvents();
  }

  public createVideoElement(): HTMLVideoElement | null | undefined {
    this._logger.info('Creating video element');
    this._renderController.createVideoElement();
    return this.getVideoElement();
  }

  public attachVideoElementEvents = () => {
    this._logger.debug('Attach event listeners');
    this._eventsHandler.toggleEvents('attach');
    this._timeUpdateController.startTimeUpdate();
  };

  public detachVideoElementEvents = (overrideVideoElement?: HTMLVideoElement) => {
    this._logger.debug('Detach event listeners');
    this._eventsHandler.toggleEvents('detach', overrideVideoElement);
  };

  public setStream(stream: TMediaStream) {
    this._logger.info('Setting media stream');
    const prevStream = this._state.getState().stream;

    if (prevStream === stream) {
      this._logger.verbose('Previous stream is equal to the new one. Skipping.');
      return;
    }

    if (stream) {
      this._streamController.updateMediaStream(stream);
    } else {
      this._streamController.removeMediaStream();
    }

    this._updated();
    this._state.setState({
      stream,
    });

    return this;
  }

  public renderVideoInto(container: Node) {
    this._logger.info('Rendering video element into container', container);
    this._renderController.renderVideoInto(container);
  }

  public setVolume(volume: number) {
    this._logger.info('Setting volume of the video element', { volume });
    this._eventBus.publish(EVideoElementControllerBusEvent.SET_VOLUME, { volume });
  }

  public getState() {
    return this._state.getState();
  }

  public destroy() {
    this._logger.info('Destroying');
    this._config = { ...defaultVideoElementControllerConfig.videoElementControllerConfig };

    this.detachVideoElementEvents();
    this._streamController.removeMediaStream();
    this._renderController.destroy();

    this._eventBus.publishSync(EVideoElementControllerBusEvent.DESTROY, {});

    this._logger.info('VideoElementController was destroyed');
    this._loggerTransport?.destroy();
  }

  public reinit() {
    this._logger.info('Reinit requested');
    [
      this._autoplayController,
      this._customEventsEmitter,
      this._playbackController,
      this._streamController,
      this._stateController,
      this._timeUpdateController,
      this._renderController,
      this._eventsHandler,
    ].forEach((inst) => {
      if (inst) {
        inst.init();
      }
    });

    this.attachVideoElementEvents();
    this._logger.info('VideoElementController was reinited');
  }

  public setLoggerLevels(
    levels: TLogLevel[],
    additionalScopes: string[] = [],
    ignoreDomains: string[] = [],
  ): void {
    this._logger.info('Setting new log level', { levels });
    if (this._loggerTransport) {
      this._loggerTransport.destroy();
      this._loggerTransport = null;
    }

    if (levels.length) {
      this._loggerTransport = new LoggerConsoleTransport(
        this._eventBus,
        levels,
        additionalScopes,
        ignoreDomains,
      );
    }
  }

  private _syncConfig() {
    this._logger.verbose('Sycronizing configs across all controllers');
    this._autoplayController?.setConfig(this._config);
    this._eventsHandler.setConfig(this._config);
    this._streamController.setConfig(this._config);
    this._timeUpdateController.setConfig(this._config);
    this._renderController.setConfig(this._config);
    this._stateController.setConfig(this._config);
  }

  private _createControllers() {
    this._logger.verbose('Creating all controllers');
    this._state = new StateManager(INITIAL_STATE);
    const propsWithState = {
      getVideoElement: this.getVideoElement,
      state: this._state,
      eventBus: this._eventBus,
    };

    this._autoplayController = new AutoplayController(propsWithState);
    this._customEventsEmitter = new CustomEventsEmitter(propsWithState);
    this._eventsHandler = new VideoEventsHandler(propsWithState);
    this._playbackController = new PlaybackController(propsWithState);
    this._streamController = new StreamController(propsWithState);
    this._stateController = new StateController(propsWithState);
    this._timeUpdateController = new TimeUpdateController(propsWithState);
    this._renderController = new RenderController(propsWithState);
    this._logger.verbose('Controllers created');
  }

  private _updated = () => {
    this._eventBus.publish(EVideoElementControllerBusEvent.UPDATED, {});
  };
}
